import { UnstyledButton } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import type { ComponentPropsWithoutRef, PointerEvent as ReactPointerEvent } from 'react';
import cn from 'classnames';

import type { SplitPaneHandle } from './SplitPane';
import classes from './SplitResizer.module.css';

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export interface SplitResizerProps
  extends Omit<
    ComponentPropsWithoutRef<'button'>,
    'style' | 'onPointerDown' | 'onChange' | 'onResize'
  > {
  /**
   * Доп. стили ресайзера (включая CSS-переменные). Внешний вид настраивается:
   * `--split-resizer-color` (цвет линии без ховера, по умолчанию прозрачная),
   * `--split-resizer-hover-color`, `--split-resizer-size`.
   * @example <Split.Resizer style={{ '--split-resizer-color': 'var(--mantine-color-gray-3)' }} />
   */
  style?: React.CSSProperties & Partial<Record<`--${string}`, string | number>>;
  /** Controlled resize mode: horizontal delta from pointer down, in pixels. */
  onResize?: (deltaX: number) => void;
  onResizeEnd?: (deltaX: number) => void;
  __beforeRef?: React.RefObject<SplitPaneHandle | null>;
  __afterRef?: React.RefObject<SplitPaneHandle | null>;
  __onSaveLeftPercent?: (percent: number) => void;
}

/**
 * Разделитель между панелями (подход как в @gfazioli/mantine-split-pane).
 * Получает refs панелей и колбек сохранения через cloneElement из Split.
 * Для отдельных панелей можно передать onResize/onResizeEnd без Split-контейнера.
 */
export function SplitResizer({
  style,
  className,
  onResize,
  onResizeEnd,
  'aria-label': ariaLabel = 'Resize',
  __beforeRef,
  __afterRef,
  __onSaveLeftPercent,
  ...buttonProps
}: SplitResizerProps) {
  const [isResizing, setIsResizing] = useState(false);
  const resizerRef = useRef<HTMLButtonElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cleanupRef.current?.(), []);

  const applyVerticalSize = (beforeWidth: number, afterWidth: number) => {
    const before = __beforeRef?.current;
    const after = __afterRef?.current;
    if (!before?.splitPane || !after?.splitPane) return;
    before.splitPane.style.width = `${beforeWidth}px`;
    after.splitPane.style.width = `${afterWidth}px`;
  };

  const parseStyleLength = (v: string): number => {
    const t = v.trim();
    const n = parseFloat(t);
    if (Number.isNaN(n)) return 0;
    if (t.endsWith('rem'))
      return n * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16);
    return n;
  };

  const getResizerLayoutWidth = (resizerEl: HTMLElement): number => {
    const w = resizerEl.getBoundingClientRect().width;
    const s = getComputedStyle(resizerEl);
    return w + parseStyleLength(s.marginLeft) + parseStyleLength(s.marginRight);
  };

  const processVerticalSize = (deltaX: number) => {
    const before = __beforeRef?.current;
    const after = __afterRef?.current;
    if (!before?.splitPane || !after?.splitPane || !resizerRef.current) return;
    const beforePane = before.splitPane;
    const container = beforePane.parentElement;
    if (!container) return;
    const total = container.getBoundingClientRect().width;
    const resizerW = getResizerLayoutWidth(resizerRef.current);
    const totalAvailable = total - resizerW;
    if (totalAvailable <= 0) return;
    const currentBefore = beforePane.getBoundingClientRect().width;
    const minB = before.getMinWidth() ?? 0;
    const maxB = before.getMaxWidth() ?? totalAvailable;
    const minA = after.getMinWidth() ?? 0;
    const maxA = after.getMaxWidth() ?? totalAvailable;
    const low = Math.max(minB, totalAvailable - maxA);
    const high = Math.min(maxB, totalAvailable - minA);
    const beforeWidth = clamp(currentBefore + deltaX, low, high);
    const afterWidth = totalAvailable - beforeWidth;
    applyVerticalSize(beforeWidth, afterWidth);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const container = __beforeRef?.current?.splitPane?.parentElement;
    if (!onResize && (!container || !__afterRef?.current?.splitPane)) return;
    e.preventDefault();
    e.stopPropagation();
    cleanupRef.current?.();
    setIsResizing(true);
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const startX = e.clientX;
    let lastDelta = 0;

    const onMove = (ev: PointerEvent) => {
      lastDelta = ev.clientX - startX;
      if (onResize) {
        onResize(lastDelta);
        return;
      }
      if (
        !__beforeRef?.current?.splitPane ||
        !__afterRef?.current?.splitPane ||
        !resizerRef.current
      )
        return;
      const resizerRect = resizerRef.current.getBoundingClientRect();
      const resizerWidth = parseFloat(getComputedStyle(resizerRef.current).width) || 0;
      const deltaX = ev.clientX - resizerRect.left - resizerWidth / 2;
      processVerticalSize(deltaX);
    };

    const cleanup = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('blur', onUp);
    };

    const onUp = () => {
      cleanup();
      cleanupRef.current = null;
      setIsResizing(false);
      onResizeEnd?.(lastDelta);

      const before = __beforeRef?.current?.splitPane;
      const after = __afterRef?.current?.splitPane;
      if (before && after && container && resizerRef.current && __onSaveLeftPercent) {
        const total = container.getBoundingClientRect().width;
        const totalAvailable = total - getResizerLayoutWidth(resizerRef.current);
        const beforeWidth = before.getBoundingClientRect().width;
        if (totalAvailable > 0) {
          __onSaveLeftPercent((beforeWidth / totalAvailable) * 100);
        }
      }
    };

    cleanupRef.current = cleanup;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('blur', onUp);
  };

  return (
    <UnstyledButton
      {...buttonProps}
      ref={resizerRef}
      type='button'
      aria-label={ariaLabel}
      data-orientation='vertical'
      data-resizing={isResizing || undefined}
      className={cn(classes.resizer, className)}
      style={{ flexShrink: 0, ...style }}
      onPointerDown={onPointerDown}
    />
  );
}
